import React from "react";
import { ChevronDown, ChevronRight, Upload } from "lucide-react";
import { AppModal, AppModalHeader } from "../../../components/AppModal";
import { APP_BTN_OUTLINE_CLASS, APP_BTN_PRIMARY_CLASS } from "../../../ui/controlStyles";
import { LoadingSpinner } from "../../../components/LoadingSpinner";
import { OfferImportSourcePicker, type OfferImportSource } from "./OfferImportSourcePicker";

/** Zeile wie vom Server (server/services/lvPdf/parseLvText.ts, LvRow). */
type LvRow = {
  kind: "title" | "item" | "text";
  oz: string;
  title_level: number | null;
  short_text: string;
  long_text: string;
  quantity: number | null;
  unit: string;
  unit_price: number | null;
  is_eventual: boolean;
  source: "rules" | "ai";
};

type JobState =
  | { status: "running"; phase: "pages" | "vision" | "sections"; done: number; total: number }
  | {
      status: "done";
      rows: LvRow[];
      filename: string;
      pageCount: number;
      skippedPages: number;
      visionPages: number;
      visionSkipped: number;
      aiSections: number;
      unverifiedSections: number;
    }
  | { status: "error"; error: string };

const POLL_MS = 2000;
/** Ab so vielen Zeilen starten Untertitel zugeklappt, damit die Vorschau bedienbar bleibt. */
const COLLAPSE_FROM_ROWS = 300;

function phaseLabel(s: Extract<JobState, { status: "running" }>): string {
  if (s.phase === "vision") return `Gescannte Seiten werden gelesen… (${s.total} Seiten)`;
  if (s.phase === "sections" && s.total > 0) return `Positionen werden geprüft… (${s.done}/${s.total} Abschnitte)`;
  return "PDF wird gelesen…";
}

function formatQty(q: number | null): string {
  if (q == null) return "";
  return q.toLocaleString("de-DE", { maximumFractionDigits: 3 });
}

async function readJson<T>(res: Response): Promise<T & { error?: string }> {
  const raw = await res.text();
  try {
    return (raw ? JSON.parse(raw) : {}) as T & { error?: string };
  } catch {
    return { error: raw.trim() } as T & { error?: string };
  }
}

/** Für jede Zeile der nächste übergeordnete Titel (Index) und die Einrückung. */
function buildTree(rows: LvRow[]): { parent: number[]; indent: number[] } {
  const parent: number[] = [];
  const indent: number[] = [];
  const stack: Array<{ index: number; level: number }> = [];
  rows.forEach((r, i) => {
    if (r.kind === "title") {
      const level = Math.max(1, r.title_level ?? 1);
      while (stack.length > 0 && stack[stack.length - 1].level >= level) stack.pop();
      parent.push(stack.length > 0 ? stack[stack.length - 1].index : -1);
      indent.push(level - 1);
      stack.push({ index: i, level });
    } else {
      parent.push(stack.length > 0 ? stack[stack.length - 1].index : -1);
      indent.push(stack.length);
    }
  });
  return { parent, indent };
}

/** LV aus PDF: Datei/Quelle wählen → Seiten lesen und Zeilen erkennen → Vorschau mit Auswahl → ins Angebot übernehmen. */
export const LvPdfImportDialog: React.FC<{
  offeringId: string;
  open: boolean;
  onClose: () => void;
  onImported?: () => void | Promise<void>;
  /** Liegt eine GAEB-Datei im Projekt, kann der Nutzer direkt dorthin wechseln. */
  onSwitchToGaeb?: () => void;
}> = ({ offeringId, open, onClose, onImported, onSwitchToGaeb }) => {
  const [jobId, setJobId] = React.useState<string | null>(null);
  const [state, setState] = React.useState<JobState | null>(null);
  const [selected, setSelected] = React.useState<Set<number>>(new Set());
  const [collapsed, setCollapsed] = React.useState<Set<number>>(new Set());
  const [busySource, setBusySource] = React.useState<OfferImportSource | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [gaebHint, setGaebHint] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const running = state?.status === "running" || busySource != null;

  const reset = () => {
    setJobId(null);
    setState(null);
    setSelected(new Set());
    setCollapsed(new Set());
    setBusySource(null);
    setError(null);
  };

  const close = () => {
    if (saving) return;
    reset();
    onClose();
  };

  // GAEB im Projekt? Die Datei ist immer genauer als das PDF.
  React.useEffect(() => {
    if (!open || !onSwitchToGaeb) return;
    let cancelled = false;
    fetch(`/api/gaeb/offerings/${encodeURIComponent(offeringId)}/import-candidates?kind=gaeb`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((data: { items?: Array<{ filename: string }> }) => {
        if (!cancelled) setGaebHint(data.items?.[0]?.filename ?? null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, offeringId, onSwitchToGaeb]);

  // Stand des Auslese-Jobs abfragen.
  React.useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const res = await fetch(
          `/api/lv-pdf/offerings/${encodeURIComponent(offeringId)}/extract/${encodeURIComponent(jobId)}`,
        );
        const data = await readJson<JobState>(res);
        if (cancelled) return;
        if (!res.ok) throw new Error(data.error || "Auslesen fehlgeschlagen.");
        setState(data);
        if (data.status === "running") {
          timer = setTimeout(poll, POLL_MS);
        } else {
          setBusySource(null);
          if (data.status === "done") {
            // Alles vorausgewählt außer Hinweistexten (Vorbemerkungen sind oft lang).
            setSelected(new Set(data.rows.map((r, i) => (r.kind === "text" ? -1 : i)).filter((i) => i >= 0)));
            if (data.rows.length > COLLAPSE_FROM_ROWS) {
              setCollapsed(
                new Set(data.rows.map((r, i) => (r.kind === "title" && (r.title_level ?? 1) >= 2 ? i : -1)).filter((i) => i >= 0)),
              );
            }
          }
        }
      } catch (e) {
        if (cancelled) return;
        setBusySource(null);
        setState({ status: "error", error: e instanceof Error ? e.message : "Auslesen fehlgeschlagen." });
      }
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [jobId, offeringId]);

  const startExtraction = async (input: File | OfferImportSource) => {
    setError(null);
    setState({ status: "running", phase: "pages", done: 0, total: 0 });
    if (!(input instanceof File)) setBusySource(input);
    try {
      let res: Response;
      if (input instanceof File) {
        const fd = new FormData();
        fd.append("file", input);
        res = await fetch(`/api/lv-pdf/offerings/${encodeURIComponent(offeringId)}/extract`, { method: "POST", body: fd });
      } else {
        res = await fetch(`/api/lv-pdf/offerings/${encodeURIComponent(offeringId)}/extract`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ source: input }),
        });
      }
      const data = await readJson<{ jobId?: string }>(res);
      if (!res.ok || !data.jobId) throw new Error(data.error || `Auslesen fehlgeschlagen (${res.status}).`);
      setJobId(data.jobId);
    } catch (e) {
      setBusySource(null);
      setState({ status: "error", error: e instanceof Error ? e.message : "Auslesen fehlgeschlagen." });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const rows = React.useMemo(() => (state?.status === "done" ? state.rows : []), [state]);
  const { parent, indent } = React.useMemo(() => buildTree(rows), [rows]);
  const visible = React.useMemo(() => {
    const out = new Array<boolean>(rows.length).fill(true);
    for (let i = 0; i < rows.length; i += 1) {
      let p = parent[i];
      while (p >= 0) {
        if (collapsed.has(p)) {
          out[i] = false;
          break;
        }
        p = parent[p];
      }
    }
    return out;
  }, [rows, parent, collapsed]);

  const subtree = (titleIndex: number): number[] => {
    const level = Math.max(1, rows[titleIndex].title_level ?? 1);
    const out: number[] = [];
    for (let j = titleIndex + 1; j < rows.length; j += 1) {
      const r = rows[j];
      if (r.kind === "title" && Math.max(1, r.title_level ?? 1) <= level) break;
      out.push(j);
    }
    return out;
  };

  const toggle = (i: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      const affected = rows[i].kind === "title" ? [i, ...subtree(i)] : [i];
      const turnOn = !next.has(i);
      for (const j of affected) {
        if (turnOn) next.add(j);
        else next.delete(j);
      }
      return next;
    });

  const toggleCollapsed = (i: number) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  const itemCount = rows.filter((r) => r.kind === "item").length;
  const selectedItems = rows.filter((r, i) => selected.has(i) && r.kind === "item").length;
  const aiRows = rows.filter((r) => r.source === "ai").length;

  const runImport = async () => {
    const chosen = rows
      .filter((_, i) => selected.has(i))
      .map(({ kind, oz, title_level, short_text, long_text, quantity, unit, unit_price, is_eventual }) => ({
        kind,
        oz,
        title_level,
        short_text,
        long_text,
        quantity,
        unit,
        unit_price,
        is_eventual,
      }));
    if (chosen.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/lv-pdf/offerings/${encodeURIComponent(offeringId)}/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rows: chosen }),
      });
      const data = await readJson<{ inserted?: number }>(res);
      if (!res.ok) throw new Error(data.error || "LV konnte nicht übernommen werden.");
      await onImported?.();
      setSaving(false);
      close();
    } catch (e) {
      setError(e instanceof Error ? e.message : "LV konnte nicht übernommen werden.");
      setSaving(false);
    }
  };

  const summary =
    state?.status === "done"
      ? [
          `${itemCount} Positionen auf ${state.pageCount} Seiten erkannt`,
          state.skippedPages > 0 ? `${state.skippedPages} Seiten ohne LV übersprungen` : null,
          state.visionPages > 0 ? `${state.visionPages} gescannte Seiten per KI gelesen` : null,
          state.visionSkipped > 0 ? `${state.visionSkipped} gescannte Seiten nicht gelesen (Limit)` : null,
          state.aiSections > 0 ? `${state.aiSections} Abschnitte per KI ergänzt (markiert)` : null,
          state.unverifiedSections > 0 ? `${state.unverifiedSections} Abschnitte mit Unstimmigkeiten, bitte prüfen` : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : "";

  return (
    <AppModal
      open={open}
      onClose={close}
      disableClose={saving}
      overlayClassName="z-[260]"
      panelClassName={state?.status === "done" ? "md:max-w-4xl" : "md:max-w-lg"}
      ariaLabelledBy="lv-pdf-dialog-title"
    >
      <AppModalHeader
        titleId="lv-pdf-dialog-title"
        title={state?.status === "done" ? `Leistungsverzeichnis aus PDF · ${state.filename}` : "Leistungsverzeichnis aus PDF"}
        onClose={close}
        closeDisabled={saving}
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {state?.status === "done" ? (
          rows.length === 0 ? (
            <p className="text-sm text-app-text-muted">Im PDF wurden keine LV-Positionen erkannt.</p>
          ) : (
            <>
              <p className="text-xs text-app-text-subtle">
                {summary}. Mengen und Texte kurz prüfen; Preise bleiben leer, wenn das PDF keine enthält.
              </p>
              <div className="rounded-lg border border-app-border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-app-surface-muted text-xs text-app-text-subtle">
                    <tr>
                      <th className="w-8 px-2 py-2">
                        <input
                          type="checkbox"
                          aria-label="Alle auswählen"
                          checked={selected.size === rows.length}
                          onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((_, i) => i)) : new Set())}
                        />
                      </th>
                      <th className="px-2 py-2 text-left font-semibold">Bezeichnung</th>
                      <th className="px-2 py-2 text-right font-semibold">Menge</th>
                      <th className="px-2 py-2 text-left font-semibold">Einheit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-app-border">
                    {rows.map((r, i) => {
                      if (!visible[i]) return null;
                      const aiBadge =
                        r.source === "ai" ? (
                          <span
                            className="ml-1.5 rounded bg-sky-100 px-1 text-[10px] font-semibold text-sky-800"
                            title="Dieser Abschnitt wurde per KI ergänzt"
                          >
                            KI
                          </span>
                        ) : null;
                      return (
                        <tr
                          key={i}
                          className={`${r.kind === "title" ? "bg-app-surface-muted/60" : ""} ${selected.has(i) ? "" : "opacity-50"}`}
                        >
                          <td className="px-2 py-1.5 align-top">
                            <input type="checkbox" checked={selected.has(i)} onChange={() => toggle(i)} />
                          </td>
                          <td className="px-2 py-1.5 align-top" style={{ paddingLeft: `${0.5 + indent[i] * 0.75}rem` }}>
                            {r.kind === "title" ? (
                              <button
                                type="button"
                                onClick={() => toggleCollapsed(i)}
                                className="inline-flex items-center gap-1 text-left font-semibold text-app-text"
                              >
                                {collapsed.has(i) ? (
                                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-app-text-subtle" />
                                ) : (
                                  <ChevronDown className="h-3.5 w-3.5 shrink-0 text-app-text-subtle" />
                                )}
                                {r.oz ? <span className="mr-1">{r.oz}</span> : null}
                                {r.short_text}
                                {aiBadge}
                              </button>
                            ) : (
                              <details>
                                <summary className="cursor-pointer list-none text-app-text">
                                  {r.kind === "text" ? <span className="italic text-app-text-muted">Hinweis: </span> : null}
                                  {r.oz ? <span className="mr-1.5 text-app-text-muted">{r.oz}</span> : null}
                                  {r.short_text || r.long_text.split("\n")[0]}
                                  {r.is_eventual ? (
                                    <span className="ml-1.5 rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-800">
                                      Bedarf
                                    </span>
                                  ) : null}
                                  {aiBadge}
                                </summary>
                                {r.long_text ? (
                                  <p className="mt-1 whitespace-pre-line text-xs text-app-text-subtle">{r.long_text}</p>
                                ) : null}
                              </details>
                            )}
                          </td>
                          <td className="px-2 py-1.5 align-top text-right tabular-nums whitespace-nowrap">
                            {r.kind === "item" ? formatQty(r.quantity) : ""}
                          </td>
                          <td className="px-2 py-1.5 align-top whitespace-nowrap">{r.kind === "item" ? r.unit : ""}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {aiRows > 0 ? (
                <p className="text-xs text-app-text-subtle">
                  Mit „KI“ markierte Zeilen stammen aus Abschnitten, in denen die Regeln nicht eindeutig waren.
                </p>
              ) : null}
            </>
          )
        ) : running ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <LoadingSpinner size="md" />
            <p className="text-sm text-app-text-muted">
              {state?.status === "running" ? phaseLabel(state) : "PDF wird geladen…"}
            </p>
            <p className="text-xs text-app-text-subtle">
              Große LVs dauern einige Sekunden, gescannte deutlich länger. Du kannst das Fenster schließen und später wiederkommen.
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm text-app-text-muted">
              Liest Titel, Positionen, Mengen und Einheiten aus einem PDF-Leistungsverzeichnis. Vor dem Übernehmen siehst du
              eine Vorschau. Das PDF wird dabei im Projekt abgelegt.
            </p>
            {gaebHint && onSwitchToGaeb ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
                <span>
                  Im Projekt liegt die GAEB-Datei „{gaebHint}“. Sie ist genauer als ein PDF.
                </span>
                <button type="button" onClick={onSwitchToGaeb} className="font-semibold underline underline-offset-2">
                  GAEB importieren
                </button>
              </div>
            ) : null}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full text-left rounded-md border border-dashed border-sky-200 bg-app-surface-muted/60 px-4 py-5 hover:bg-sky-50/50 hover:border-sky-300 transition-colors"
            >
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center text-sky-600">
                  <Upload className="w-5 h-5" />
                </div>
                <p className="text-sm text-app-text-muted">
                  PDF hier auswählen oder{" "}
                  <span className="text-sky-700 font-semibold underline underline-offset-2">Datei wählen</span>
                </p>
                <p className="text-xs text-app-text-subtle">PDF bis 2.000 Seiten (max. 50 MB)</p>
              </div>
            </button>
            <OfferImportSourcePicker
              offeringId={offeringId}
              kind="pdf"
              disabled={running}
              busySource={busySource}
              onPick={(source) => void startExtraction(source)}
            />
          </>
        )}

        {state?.status === "error" ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{state.error}</div>
        ) : null}
        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        ) : null}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,application/pdf"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void startExtraction(f);
        }}
      />

      {state?.status === "done" || state?.status === "error" ? (
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-app-border px-5 py-3">
          <button type="button" onClick={reset} disabled={saving} className={APP_BTN_OUTLINE_CLASS}>
            Anderes PDF
          </button>
          {state.status === "done" && rows.length > 0 ? (
            <button
              type="button"
              disabled={saving || selected.size === 0}
              onClick={() => void runImport()}
              className={APP_BTN_PRIMARY_CLASS}
            >
              {saving ? <LoadingSpinner size="sm" /> : null}
              {saving ? "Übernehme…" : `${selectedItems} Positionen übernehmen`}
            </button>
          ) : null}
        </div>
      ) : null}
    </AppModal>
  );
};
